import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export type SupportedCurrency = 'EUR' | 'PLN';

export interface ExchangeRateData {
  base: 'EUR';
  target: 'PLN';
  rate: number;
  plnToEurRate: number;
  effectiveDate: string;
  timestamp: number;
  source: string;
}

@Injectable({
  providedIn: 'root',
})
export class CurrencyService {
  private readonly defaultCurrency: SupportedCurrency = 'EUR';
  private readonly currencySubject = new BehaviorSubject<SupportedCurrency>(this.defaultCurrency);
  private readonly rateSubject = new BehaviorSubject<number>(0);
  private readonly effectiveDateSubject = new BehaviorSubject<string>('');

  /** Emits whenever active currency changes */
  readonly currency$: Observable<SupportedCurrency> = this.currencySubject.asObservable();
  /** Emits latest EUR -> PLN exchange rate */
  readonly rate$: Observable<number> = this.rateSubject.asObservable();

  /** Whether the user manually selected their currency (prevents address changes from overriding their choice) */
  private isUserManualSelection = false;

  private rateFetch$?: Observable<ExchangeRateData>;

  constructor(private readonly http: HttpClient) {
    this.initFromStorage();
    this.fetchLiveRate().subscribe({
      error: (error) => console.warn(error.message),
    });
  }

  private initFromStorage(): void {
    try {
      const savedCurrency = localStorage.getItem('brocom_selected_currency');
      if (savedCurrency === 'PLN' || savedCurrency === 'EUR') {
        this.currencySubject.next(savedCurrency);
        this.isUserManualSelection = true;
        return;
      }

      const savedCountry = localStorage.getItem('brocom_selected_country');
      if (savedCountry) {
        const parsed = typeof savedCountry === 'string' && savedCountry.startsWith('{')
          ? JSON.parse(savedCountry)
          : { code: savedCountry };
        const code = (parsed?.code || '').toUpperCase();
        if (code === 'PL') {
          this.currencySubject.next('PLN');
        }
      }
    } catch (_) {}
  }

  get currentCurrency(): SupportedCurrency {
    return this.currencySubject.value;
  }

  get currentRate(): number {
    return this.rateSubject.value;
  }

  get isManualSelection(): boolean {
    return this.isUserManualSelection;
  }

  /**
   * Fetches official live exchange rate from backend (which proxies Narodowy Bank Polski),
   * with a direct NBP request as a live-provider fallback if the backend endpoint fails.
   */
  fetchLiveRate(): Observable<ExchangeRateData> {
    if (!this.rateFetch$) {
      const backendUrl = `${environment.apiUrl}/currency/rates`;
      this.rateFetch$ = this.http.get<ExchangeRateData>(backendUrl).pipe(
        tap((data) => {
          if (!data || !Number.isFinite(Number(data.rate)) || Number(data.rate) <= 0) {
            throw new Error('Invalid exchange rate response');
          }
          this.rateSubject.next(Number(data.rate));
          if (data.effectiveDate) {
            this.effectiveDateSubject.next(data.effectiveDate);
          }
        }),
        catchError((err) => {
          console.warn('Backend /currency/rates endpoint returned error (e.g. 404), falling back to direct NBP API:', err?.status || err?.message);
          // Direct browser call to public CORS-enabled NBP API
          return this.http.get<any>('https://api.nbp.pl/api/exchangerates/rates/a/eur/?format=json').pipe(
            map((nbpRes) => {
              const mid = Number(nbpRes?.rates?.[0]?.mid);
              const effectiveDate = nbpRes?.rates?.[0]?.effectiveDate || '';
              if (mid && mid > 0) {
                this.rateSubject.next(mid);
                if (effectiveDate) this.effectiveDateSubject.next(effectiveDate);
                return {
                  base: 'EUR' as const,
                  target: 'PLN' as const,
                  rate: mid,
                  plnToEurRate: Number((1 / mid).toFixed(4)),
                  effectiveDate,
                  timestamp: Date.now(),
                  source: 'Narodowy Bank Polski (Direct)',
                };
              }
              throw new Error('Invalid rate');
            }),
            catchError(() => {
              this.rateFetch$ = undefined;
              return throwError(
                () => new Error('Live EUR/PLN exchange rate is unavailable. Please try again later.'),
              );
            })
          );
        }),
        shareReplay(1),
      );
    }
    return this.rateFetch$;
  }

  /**
   * Set active currency manually or automatically.
   * If isManual is true, user's choice is locked against automatic country-based switching.
   */
  setCurrency(currency: SupportedCurrency, isManual = false): void {
    if (isManual) {
      this.isUserManualSelection = true;
    }
    try {
      localStorage.setItem('brocom_selected_currency', currency);
    } catch (_) {}
    if (this.currencySubject.value !== currency) {
      this.currencySubject.next(currency);
    }
  }

  /**
   * Automatically changes currency based on country selection:
   * Poland ('PL') -> PLN
   * Any other country -> EUR
   * (Skipped if the user has manually toggled the currency switch)
   */
  onCountryChange(countryCode: string): void {
    if (this.isUserManualSelection) {
      return;
    }

    const code = (countryCode || '').trim().toUpperCase();
    if (code === 'PL' || code === 'POLAND' || code === 'POLSKA') {
      if (this.currencySubject.value !== 'PLN') {
        try {
          localStorage.setItem('brocom_selected_currency', 'PLN');
        } catch (_) {}
        this.currencySubject.next('PLN');
      }
    } else if (code.length > 0) {
      if (this.currencySubject.value !== 'EUR') {
        try {
          localStorage.setItem('brocom_selected_currency', 'EUR');
        } catch (_) {}
        this.currencySubject.next('EUR');
      }
    }
  }

  /**
   * Reset manual override lock (e.g. if shopping session is reset)
   */
  resetManualOverride(): void {
    this.isUserManualSelection = false;
  }

  /**
   * Converts an amount stored in EUR (like product base priceHT) to the currently active currency.
   */
  convertFromEur(
    amountInEur: number | undefined | null,
    targetCurrency: SupportedCurrency = this.currentCurrency,
  ): number {
    const val = Number(amountInEur) || 0;
    if (targetCurrency === 'PLN') {
      if (this.currentRate <= 0) return Number.NaN;
      return Number((val * this.currentRate).toFixed(2));
    }
    return Number(val.toFixed(2));
  }

  /**
   * Convert shipping quote to the active display currency.
   * If shipping is quoted in PLN and current currency is EUR, or vice-versa.
   */
  convertShipping(
    shippingCost: number | undefined | null,
    quoteCurrency: string,
    targetCurrency: SupportedCurrency = this.currentCurrency,
  ): number {
    const cost = Number(shippingCost) || 0;
    const from = (quoteCurrency || 'EUR').toUpperCase();
    const to = targetCurrency;

    if (from === to) {
      return Number(cost.toFixed(2));
    }

    if (from === 'EUR' && to === 'PLN') {
      if (this.currentRate <= 0) return Number.NaN;
      return Number((cost * this.currentRate).toFixed(2));
    }

    if (from === 'PLN' && to === 'EUR') {
      if (this.currentRate <= 0) return Number.NaN;
      return Number((cost / this.currentRate).toFixed(2));
    }

    return Number(cost.toFixed(2));
  }

  /**
   * Formats a monetary value according to French/European standard:
   * 1 250,50 € or 1 250,50 zł
   */
  format(amount: number | undefined | null, currencyCode?: string): string {
    const val = Number(amount) || 0;
    const targetCurrency = (currencyCode || this.currentCurrency).toUpperCase();
    const symbol = targetCurrency === 'PLN' ? 'zł' : '€';

    const formatted = val.toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return `${formatted} ${symbol}`;
  }
}
